import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Minus, Plus, ShoppingBag, ArrowLeft, CreditCard, Phone, CheckCircle, Globe, Copy, Send, Building2, Loader2, Banknote, AlertCircle, Truck, MapPinned, Home, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { SHIPPING_COUNTRIES, MEETING_POINT_NAMES, MEXICAN_STATES } from '../utils/constants';
import { validatePhoneNumber } from '../utils/helpers';
import { BankTransferDetails } from '../components/PaymentMethodSelector';
import { trackBeginCheckout, trackPurchase, trackAddPaymentInfo } from '../hooks/useGoogleAnalytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Cart = () => {
  const { cart, updateCartItem, removeFromCart, loading, refreshCart } = useCart();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [productStocks, setProductStocks] = useState({});
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [orderSuccess, setOrderSuccess] = useState(null);
  const [shippingCountry, setShippingCountry] = useState('MX');
  const [bankConfig, setBankConfig] = useState(null);
  const [bankConfigLoading, setBankConfigLoading] = useState(true);
  
  // Delivery method state
  const [deliveryMethod, setDeliveryMethod] = useState('meeting_point');
  const [meetingPoints, setMeetingPoints] = useState([]);
  const [selectedMeetingPoint, setSelectedMeetingPoint] = useState('');
  const [showAddressForm, setShowAddressForm] = useState(false);
  
  // Shipping address state
  const [shippingAddress, setShippingAddress] = useState({
    street: '',
    exterior_number: '',
    interior_number: '',
    neighborhood: '',
    municipality: '',
    state: '',
    postal_code: '',
    references: ''
  });
  
  // Load user's saved address
  useEffect(() => {
    if (user?.shipping_address) {
      setShippingAddress(user.shipping_address);
    }
  }, [user]);
  
  // Fetch meeting points configuration
  useEffect(() => {
    const fetchMeetingPoints = async () => {
      try {
        const response = await axios.get(`${API}/config/meeting-points`);
        setMeetingPoints(response.data.meeting_points || []);
      } catch (error) {
        setMeetingPoints([]);
        // Meeting points config not available - silent fail (configuration endpoint)
      }
    };
    fetchMeetingPoints();
  }, []);
  
  // Fetch bank transfer configuration from backend
  useEffect(() => {
    const fetchBankConfig = async () => {
      try {
        const response = await axios.get(`${API}/config/bank-transfer`);
        setBankConfig(response.data);
      } catch (error) {
        setBankConfig(null);
        // Bank transfer not configured - option will be hidden
      } finally {
        setBankConfigLoading(false);
      }
    };
    fetchBankConfig();
  }, []);
  
  // Reset payment method to card if COD selected and country changes to non-Mexico
  useEffect(() => {
    if (paymentMethod === 'cash_on_delivery' && shippingCountry !== 'MX') {
      setPaymentMethod('card');
    }
  }, [shippingCountry, paymentMethod]);
  
  // Cash on delivery only allows meeting point
  useEffect(() => {
    if (paymentMethod === 'cash_on_delivery') {
      setDeliveryMethod('meeting_point');
    }
  }, [paymentMethod]);
  
  // Reset payment method if bank_transfer selected but config not available
  useEffect(() => {
    if (paymentMethod === 'bank_transfer' && !bankConfigLoading && !bankConfig) {
      setPaymentMethod('card');
    }
  }, [paymentMethod, bankConfig, bankConfigLoading]);

  // Fetch current stock for all cart items
  useEffect(() => {
    const fetchStocks = async () => {
      if (cart.items.length > 0) {
        try {
          const stockPromises = cart.items.map(item =>
            axios.get(`${API}/products/${item.product_id}`)
          );
          const responses = await Promise.all(stockPromises);
          const stocks = {};
          responses.forEach((res, index) => {
            stocks[cart.items[index].product_id] = res.data.stock;
          });
          setProductStocks(stocks);
        } catch (error) {
          setProductStocks({});
          // Stock validation will happen at checkout - silent fail for background fetch
        }
      }
    };
    fetchStocks();
  }, [cart.items]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(price) + ' MXN';
  };

  // Helper function to get payment method label (replaces nested ternary)
  const getPaymentMethodLabel = (method) => {
    const labels = {
      'card': 'Tarjeta',
      'bank_transfer': 'Transferencia',
      'cash_on_delivery': 'Contra Entrega'
    };
    return labels[method] || method;
  };

  // Helper function to get delivery method label
  const getDeliveryMethodLabel = (method) => {
    return method === 'meeting_point' ? 'Punto de Encuentro' : 'Envío a Domicilio';
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Only allow numbers, +, spaces, and dashes
    const filtered = value.replace(/[^0-9+\s-]/g, '');
    setPhoneNumber(filtered);
    
    if (filtered && !validatePhoneNumber(filtered)) {
      setPhoneError('Ingresa un número válido (mín. 10 dígitos)');
    } else {
      setPhoneError('');
    }
  };

  const handleQuantityChange = async (productId, newQuantity) => {
    const maxStock = productStocks[productId] || 999;
    
    if (newQuantity < 1) {
      await removeFromCart(productId);
      toast.success('Producto eliminado del carrito');
    } else if (newQuantity > maxStock) {
      toast.error(`Solo hay ${maxStock} unidades disponibles`);
    } else {
      await updateCartItem(productId, newQuantity);
    }
  };

  const handleRemove = async (productId) => {
    await removeFromCart(productId);
    toast.success('Producto eliminado del carrito');
  };

  const handleCheckout = async () => {
    if (cart.items.length === 0) {
      toast.error('Tu carrito está vacío');
      return;
    }

    // Validate phone number
    if (!phoneNumber) {
      setPhoneError('El número de WhatsApp es requerido');
      toast.error('Por favor ingresa tu número de WhatsApp');
      return;
    }

    if (!validatePhoneNumber(phoneNumber)) {
      setPhoneError('Ingresa un número válido (mín. 10 dígitos)');
      toast.error('Por favor ingresa un número de teléfono válido');
      return;
    }
    
    // Validate delivery method
    if (deliveryMethod === 'meeting_point' && !selectedMeetingPoint) {
      toast.error('Por favor selecciona un punto de encuentro');
      return;
    }
    
    if (deliveryMethod === 'shipping') {
      // Validate required shipping address fields
      const requiredFields = ['street', 'exterior_number', 'neighborhood', 'municipality', 'state', 'postal_code'];
      const missingFields = requiredFields.filter(field => !shippingAddress[field]?.trim());
      if (missingFields.length > 0) {
        toast.error('Por favor completa todos los campos de la dirección');
        return;
      }
      if (shippingAddress.postal_code.length !== 5) {
        toast.error('El código postal debe tener 5 dígitos');
        return;
      }
    }

    try {
      setCheckoutLoading(true);
      
      // Track begin_checkout event
      trackBeginCheckout(cart);
      
      // Track payment method selection
      trackAddPaymentInfo(paymentMethod, cart);
      
      // Build delivery data
      const deliveryData = {
        delivery_method: deliveryMethod,
        meeting_point: deliveryMethod === 'meeting_point' ? selectedMeetingPoint : null,
        shipping_address: deliveryMethod === 'shipping' ? shippingAddress : null
      };
      
      // Prepare GA4 items for purchase tracking
      const ga4Items = cart.items.map(item => ({
        item_id: item.product_id || item.id,
        item_name: item.name,
        item_category: item.category || '',
        price: parseFloat(item.price) || 0,
        quantity: item.quantity
      }));
      
      if (paymentMethod === 'card') {
        // Stripe checkout - purchase event will be fired in CheckoutSuccess page
        const originUrl = window.location.origin;
        
        // Store cart items in sessionStorage for purchase tracking after Stripe redirect
        sessionStorage.setItem('ga4_pending_purchase_items', JSON.stringify(ga4Items));
        sessionStorage.setItem('ga4_pending_purchase_value', cart.total.toString());
        
        const response = await axios.post(
          `${API}/checkout/create-session`,
          { 
            origin_url: originUrl,
            phone_number: phoneNumber.replace(/[\s-]/g, ''),
            ...deliveryData
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.url) {
          window.location.href = response.data.url;
        }
      } else {
        // Cash on delivery or bank transfer
        const response = await axios.post(
          `${API}/checkout/cash-order`,
          { 
            phone_number: phoneNumber.replace(/[\s-]/g, ''),
            payment_method: paymentMethod,
            country: shippingCountry,
            ...deliveryData
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Track GA4 purchase event for bank transfer / cash on delivery
        trackPurchase({
          transaction_id: response.data.order_id || response.data.id,
          value: cart.total,
        }, ga4Items);

        // Show success state
        setOrderSuccess(response.data);
        await refreshCart(); // Refresh cart (should be empty now)
        toast.success('¡Pedido registrado exitosamente!');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al procesar el pedido';
      toast.error(errorMsg);
    } finally {
      setCheckoutLoading(false);
    }
  };
  
  // Handle address field change
  const handleAddressChange = (field, value) => {
    setShippingAddress(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDownloadTicket = async (orderId) => {
    try {
      const response = await axios.get(
        `${API}/orders/${orderId}/ticket-base64`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Create download link
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${response.data.pdf_base64}`;
      link.download = response.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Ticket descargado');
    } catch (error) {
      toast.error('Error al descargar el ticket');
    }
  };

  // Copy CLABE to clipboard
  const handleCopyClabe = async () => {
    if (!bankConfig?.clabe) return;
    
    try {
      await navigator.clipboard.writeText(bankConfig.clabe);
      toast.success('CLABE copiada al portapapeles');
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = bankConfig.clabe;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('CLABE copiada al portapapeles');
    }
  };

  // Generate WhatsApp link for sending payment proof
  const generateWhatsAppProofLink = (order, customerName) => {
    const whatsappNumber = bankConfig?.whatsapp_number?.replace(/[^0-9]/g, '') || '';
    const message = encodeURIComponent(
      `Hola, soy ${customerName}.\n` +
      `Adjunto mi comprobante de transferencia para el pedido ${order.order_number}.\n` +
      `Monto: $${order.total.toLocaleString('es-MX')} MXN.`
    );
    return `https://wa.me/${whatsappNumber}?text=${message}`;
  };

  // Order success view
  if (orderSuccess) {
    const order = orderSuccess.order;
    const isBankTransfer = order.payment_method === 'bank_transfer';
    
    return (
      <div className="pt-20 min-h-screen bg-[#FAFAF9]" data-testid="order-success-page">
        <div className="container-luxury py-16">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#E7E5E4]">
              {/* Success Header */}
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                
                <h1 
                  className="text-3xl font-light text-[#1C1917] mb-2"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  ¡Pedido Registrado!
                </h1>
                
                <p className="text-[#57534E]">
                  {orderSuccess.message}
                </p>
              </div>
              
              {/* Order Summary */}
              <div className="bg-[#FDF8F6] rounded-xl p-6 mb-6">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-[#A8A29E]">Número de pedido</p>
                    <p className="font-medium text-[#1C1917]">{order.order_number}</p>
                  </div>
                  <div>
                    <p className="text-[#A8A29E]">Total</p>
                    <p className="font-medium text-[#BA7B7C] text-lg">${order.total.toLocaleString('es-MX')} MXN</p>
                  </div>
                  <div>
                    <p className="text-[#A8A29E]">Método de pago</p>
                    <p className="font-medium text-[#1C1917]">
                      {order.payment_method === 'cash_on_delivery' ? 'Pago Contra Entrega' : 'Transferencia Bancaria'}
                    </p>
                  </div>
                  <div>
                    <p className="text-[#A8A29E]">Estado</p>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                      isBankTransfer 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {isBankTransfer ? 'Esperando comprobante' : 'Pendiente de pago'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Bank Transfer Details Section */}
              {isBankTransfer && bankConfig && (
                <div className="mb-6">
                  {/* Bank Details Card */}
                  <div className="bg-gradient-to-br from-[#1C1917] to-[#44403C] rounded-xl p-6 text-white mb-4">
                    <div className="flex items-center space-x-2 mb-4">
                      <Building2 className="w-5 h-5 text-[#D4A5A5]" />
                      <h3 className="font-medium">Datos para Transferencia</h3>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wide">Banco</p>
                        <p className="font-medium">{bankConfig.bank_name}</p>
                      </div>
                      
                      <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wide">Titular de la cuenta</p>
                        <p className="font-medium">{bankConfig.account_holder}</p>
                      </div>
                      
                      <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wide">CLABE Interbancaria</p>
                        <div className="flex items-center justify-between">
                          <p className="font-mono font-medium text-lg tracking-wider">{bankConfig.clabe}</p>
                          <button
                            onClick={handleCopyClabe}
                            className="flex items-center space-x-1 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-sm"
                            data-testid="copy-clabe-btn"
                          >
                            <Copy className="w-4 h-4" />
                            <span>Copiar</span>
                          </button>
                        </div>
                      </div>
                      
                      {bankConfig.account_number && (
                        <div>
                          <p className="text-xs text-gray-400 uppercase tracking-wide">Número de cuenta</p>
                          <p className="font-mono font-medium">{bankConfig.account_number}</p>
                        </div>
                      )}
                      
                      <div className="pt-2 border-t border-white/20">
                        <p className="text-xs text-gray-400 uppercase tracking-wide">Referencia (Número de pedido)</p>
                        <p className="font-mono font-medium text-[#D4A5A5]">{order.order_number}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Instructions */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                    <p className="text-sm text-blue-800 leading-relaxed">
                      <strong>Importante:</strong> Por favor realiza la transferencia utilizando los datos anteriores y envía tu comprobante por WhatsApp para validar tu pedido. Tu pedido será procesado una vez confirmado el pago.
                    </p>
                  </div>
                  
                  {/* WhatsApp Button */}
                  {bankConfig.whatsapp_number && (
                    <a
                      href={generateWhatsAppProofLink(order, order.user_name)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center justify-center space-x-2 py-4 bg-[#25D366] hover:bg-[#1DA851] text-white rounded-xl font-medium transition-colors"
                      data-testid="send-proof-whatsapp-btn"
                    >
                      <Send className="w-5 h-5" />
                      <span>Enviar comprobante por WhatsApp</span>
                    </a>
                  )}
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={() => handleDownloadTicket(order.id)}
                  className="w-full btn-primary flex items-center justify-center space-x-2"
                  data-testid="download-ticket-btn"
                >
                  <CreditCard className="w-5 h-5" />
                  <span>Descargar Ticket PDF</span>
                </button>
                
                <Link
                  to="/orders"
                  className="block w-full py-3 text-center text-[#57534E] hover:text-[#BA7B7C] transition-colors"
                >
                  Ver mis pedidos
                </Link>
                
                <Link
                  to="/shop"
                  className="block w-full py-3 text-center text-[#BA7B7C] hover:text-[#A36566] transition-colors"
                >
                  Seguir comprando
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="pt-20 min-h-screen bg-[#FAFAF9]">
        <div className="container-luxury py-16">
          <div className="flex items-center justify-center">
            <div className="w-12 h-12 border-2 border-[#BA7B7C] border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-[#FAFAF9]" data-testid="cart-page">
      <div className="container-luxury py-8 md:py-16">
        {/* Back Link */}
        <Link 
          to="/shop" 
          className="inline-flex items-center space-x-2 text-sm text-[#57534E] hover:text-[#BA7B7C] transition-colors mb-8"
          data-testid="back-to-shop"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Continuar comprando</span>
        </Link>

        <h1 
          className="text-4xl md:text-5xl font-light tracking-tight text-[#1C1917] mb-12"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Tu Carrito
        </h1>

        {cart.items.length === 0 ? (
          <div className="text-center py-20">
            <ShoppingBag className="w-16 h-16 mx-auto text-[#E7E5E4] mb-6" />
            <p className="text-lg text-[#A8A29E] mb-8">Tu carrito está vacío</p>
            <Link to="/shop" className="btn-primary" data-testid="start-shopping-btn">
              Empezar a comprar
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-6" data-testid="cart-items">
              {cart.items.map((item) => {
                const maxStock = productStocks[item.product_id] || 999;
                const isMaxStock = item.quantity >= maxStock;
                
                return (
                  <div 
                    key={item.product_id} 
                    className="bg-white rounded-2xl p-4 md:p-6 flex gap-4 md:gap-6 shadow-soft"
                    data-testid={`cart-item-${item.product_id}`}
                  >
                    {/* Product Image */}
                    <Link 
                      to={`/product/${item.product_id}`}
                      className="w-24 h-24 md:w-32 md:h-32 flex-shrink-0"
                    >
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-full h-full object-cover rounded-xl"
                      />
                    </Link>

                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      <Link 
                        to={`/product/${item.product_id}`}
                        className="hover:text-[#BA7B7C] transition-colors"
                      >
                        <h3 
                          className="text-lg font-light text-[#1C1917] truncate"
                          style={{ fontFamily: "'Cormorant Garamond', serif" }}
                        >
                          {item.name}
                        </h3>
                      </Link>
                      <p className="text-sm text-[#A8A29E] mt-1">
                        {formatPrice(item.price)} c/u
                      </p>
                      
                      {/* Stock Warning */}
                      {maxStock <= 3 && (
                        <p className="text-xs text-amber-600 mt-1">
                          Solo {maxStock} disponible{maxStock > 1 ? 's' : ''}
                        </p>
                      )}

                      <div className="flex items-center justify-between mt-4">
                        {/* Quantity Controls */}
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => handleQuantityChange(item.product_id, item.quantity - 1)}
                            className="w-8 h-8 rounded-full border border-[#E7E5E4] flex items-center justify-center hover:border-[#BA7B7C] transition-colors"
                            data-testid={`decrease-${item.product_id}`}
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="w-8 text-center text-sm">{item.quantity}</span>
                          <button
                            onClick={() => handleQuantityChange(item.product_id, item.quantity + 1)}
                            disabled={isMaxStock}
                            className={`w-8 h-8 rounded-full border flex items-center justify-center transition-colors ${
                              isMaxStock 
                                ? 'border-gray-200 text-gray-300 cursor-not-allowed' 
                                : 'border-[#E7E5E4] hover:border-[#BA7B7C]'
                            }`}
                            data-testid={`increase-${item.product_id}`}
                            title={isMaxStock ? `Máximo ${maxStock} disponible${maxStock > 1 ? 's' : ''}` : ''}
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>

                        {/* Remove Button */}
                        <button
                          onClick={() => handleRemove(item.product_id)}
                          className="text-[#A8A29E] hover:text-red-500 transition-colors"
                          data-testid={`remove-${item.product_id}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    {/* Subtotal */}
                    <div className="hidden md:block text-right">
                      <p className="text-lg font-medium text-[#1C1917]">
                        {formatPrice(item.subtotal)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-[#F5F0EB] rounded-2xl p-6 sticky top-24" data-testid="cart-summary">
                <h2 
                  className="text-xl font-light text-[#1C1917] mb-6"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Resumen del pedido
                </h2>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#57534E]">Subtotal ({cart.item_count} producto{cart.item_count > 1 ? 's' : ''})</span>
                    <span className="text-[#1C1917]">{formatPrice(cart.total)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#57534E]">Envío</span>
                    <span className="text-[#BA7B7C]">Entrega personal</span>
                  </div>
                </div>

                <div className="border-t border-[#E7E5E4] pt-4 mb-6">
                  <div className="flex justify-between">
                    <span className="text-[#1C1917] font-medium">Total</span>
                    <span className="text-xl font-medium text-[#1C1917]">{formatPrice(cart.total)}</span>
                  </div>
                </div>

                {/* Phone Number Field */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-[#57534E] mb-2">
                    <div className="flex items-center space-x-2">
                      <Phone className="w-4 h-4 text-[#25D366]" />
                      <span>WhatsApp (requerido)</span>
                    </div>
                  </label>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    placeholder="+52 55 1234 5678"
                    className={`w-full px-4 py-3 rounded-xl border ${
                      phoneError ? 'border-red-400' : 'border-[#E7E5E4]'
                    } focus:border-[#BA7B7C] focus:outline-none text-sm`}
                    data-testid="phone-input"
                  />
                  {phoneError && (
                    <p className="text-xs text-red-500 mt-1">{phoneError}</p>
                  )}
                  <p className="text-xs text-[#A8A29E] mt-1">
                    Te contactaremos por WhatsApp para coordinar la entrega
                  </p>
                </div>

                {/* Shipping Country Selector */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-[#57534E] mb-2">
                    <div className="flex items-center space-x-2">
                      <Globe className="w-4 h-4 text-[#BA7B7C]" />
                      <span>País de envío</span>
                    </div>
                  </label>
                  <select
                    value={shippingCountry}
                    onChange={(e) => setShippingCountry(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-[#E7E5E4] focus:border-[#BA7B7C] focus:outline-none text-sm bg-white"
                    data-testid="country-selector"
                  >
                    {SHIPPING_COUNTRIES.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.flag} {country.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Payment Method Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-[#57534E] mb-3">
                    Método de pago
                  </label>
                  <div className="space-y-2">
                    {/* Credit Card */}
                    <label 
                      className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        paymentMethod === 'card' 
                          ? 'border-[#BA7B7C] bg-[#FDF8F6]' 
                          : 'border-[#E7E5E4] hover:border-[#D4A5A5]'
                      }`}
                    >
                      <input
                        type="radio"
                        name="payment_method"
                        value="card"
                        checked={paymentMethod === 'card'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="sr-only"
                      />
                      <CreditCard className={`w-5 h-5 mr-3 ${paymentMethod === 'card' ? 'text-[#BA7B7C]' : 'text-[#A8A29E]'}`} />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-[#1C1917]">Tarjeta de Crédito/Débito</p>
                        <p className="text-xs text-[#A8A29E]">Pago seguro con Stripe</p>
                      </div>
                      {paymentMethod === 'card' && (
                        <CheckCircle className="w-5 h-5 text-[#BA7B7C]" />
                      )}
                    </label>

                    {/* Bank Transfer - Only available when config is set */}
                    {bankConfigLoading ? (
                      <div className="flex items-center p-4 rounded-xl border-2 border-[#E7E5E4] bg-gray-50 opacity-60">
                        <Loader2 className="w-5 h-5 mr-3 text-[#A8A29E] animate-spin" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-500">Transferencia Bancaria</p>
                          <p className="text-xs text-gray-400">Cargando...</p>
                        </div>
                      </div>
                    ) : bankConfig ? (
                      <label 
                        className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          paymentMethod === 'bank_transfer' 
                            ? 'border-[#BA7B7C] bg-[#FDF8F6]' 
                            : 'border-[#E7E5E4] hover:border-[#D4A5A5]'
                        }`}
                      >
                        <input
                          type="radio"
                          name="payment_method"
                          value="bank_transfer"
                          checked={paymentMethod === 'bank_transfer'}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          className="sr-only"
                        />
                        <Building2 className={`w-5 h-5 mr-3 ${paymentMethod === 'bank_transfer' ? 'text-[#BA7B7C]' : 'text-[#A8A29E]'}`} />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-[#1C1917]">Transferencia Bancaria</p>
                          <p className="text-xs text-[#A8A29E]">SPEI o transferencia</p>
                        </div>
                        {paymentMethod === 'bank_transfer' && (
                          <CheckCircle className="w-5 h-5 text-[#BA7B7C]" />
                        )}
                      </label>
                    ) : null}

                    {/* Cash on Delivery - Only available in Mexico */}
                    <label 
                      className={`flex items-center p-4 rounded-xl border-2 transition-all ${
                        shippingCountry !== 'MX'
                          ? 'border-[#E7E5E4] bg-gray-50 cursor-not-allowed opacity-60'
                          : paymentMethod === 'cash_on_delivery' 
                            ? 'border-[#BA7B7C] bg-[#FDF8F6] cursor-pointer' 
                            : 'border-[#E7E5E4] hover:border-[#D4A5A5] cursor-pointer'
                      }`}
                    >
                      <input
                        type="radio"
                        name="payment_method"
                        value="cash_on_delivery"
                        checked={paymentMethod === 'cash_on_delivery'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        disabled={shippingCountry !== 'MX'}
                        className="sr-only"
                      />
                      <Banknote className={`w-5 h-5 mr-3 ${
                        shippingCountry !== 'MX' 
                          ? 'text-gray-400' 
                          : paymentMethod === 'cash_on_delivery' 
                            ? 'text-[#BA7B7C]' 
                            : 'text-[#A8A29E]'
                      }`} />
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${shippingCountry !== 'MX' ? 'text-gray-500' : 'text-[#1C1917]'}`}>
                          Pago Contra Entrega
                        </p>
                        <p className={`text-xs ${shippingCountry !== 'MX' ? 'text-gray-400' : 'text-[#A8A29E]'}`}>
                          Paga al recibir (solo México)
                        </p>
                      </div>
                      {paymentMethod === 'cash_on_delivery' && shippingCountry === 'MX' && (
                        <CheckCircle className="w-5 h-5 text-[#BA7B7C]" />
                      )}
                    </label>
                    
                    {/* Mexico-only notice for COD */}
                    {shippingCountry !== 'MX' && (
                      <div className="flex items-start space-x-2 p-3 bg-amber-50 rounded-lg border border-amber-200" data-testid="cod-mexico-notice">
                        <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-amber-800">
                          Pago Contra Entrega solo está disponible para envíos dentro de México.
                        </p>
                      </div>
                    )}
                    
                    {/* Bank Transfer Details Preview */}
                    {paymentMethod === 'bank_transfer' && bankConfig && (
                      <div className="mt-4 space-y-3" data-testid="bank-transfer-details">
                        {/* Bank Details Card */}
                        <div className="bg-gradient-to-br from-[#1C1917] to-[#44403C] rounded-xl p-4 text-white">
                          <div className="flex items-center space-x-2 mb-3">
                            <Building2 className="w-4 h-4 text-[#D4A5A5]" />
                            <h4 className="text-sm font-medium">Datos para Transferencia</h4>
                          </div>
                          
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-400">Banco:</span>
                              <span className="font-medium">{bankConfig.bank_name}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-400">Titular:</span>
                              <span className="font-medium">{bankConfig.account_holder}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-400">CLABE:</span>
                              <div className="flex items-center space-x-2">
                                <span className="font-mono font-medium text-[#D4A5A5]">{bankConfig.clabe}</span>
                                <button
                                  type="button"
                                  onClick={handleCopyClabe}
                                  className="p-1 hover:bg-white/10 rounded transition-colors"
                                  data-testid="copy-clabe-checkout-btn"
                                  title="Copiar CLABE"
                                >
                                  <Copy className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                            {bankConfig.account_number && (
                              <div className="flex justify-between">
                                <span className="text-gray-400">Cuenta:</span>
                                <span className="font-mono font-medium">{bankConfig.account_number}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Transfer Instructions */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <p className="text-xs text-blue-800 leading-relaxed">
                            <strong>Importante:</strong> Por favor realiza la transferencia utilizando los datos anteriores y envía tu comprobante por WhatsApp para validar tu pedido. Tu pedido será procesado una vez confirmado el pago.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Delivery Method Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-[#57534E] mb-3">
                    <div className="flex items-center space-x-2">
                      <Truck className="w-4 h-4 text-[#BA7B7C]" />
                      <span>Método de entrega</span>
                    </div>
                  </label>
                  
                  {/* Cash on delivery = Meeting point only */}
                  {paymentMethod === 'cash_on_delivery' ? (
                    <div className="space-y-3">
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                        <p className="text-xs text-amber-800">
                          El pago contra entrega solo está disponible para recoger en punto de encuentro.
                        </p>
                      </div>
                      
                      {/* Meeting Points Selection for COD */}
                      <div className="space-y-2">
                        <p className="text-sm text-[#57534E] mb-2">Selecciona el punto de encuentro:</p>
                        {meetingPoints.map((point) => (
                          <label 
                            key={point.id}
                            className={`flex items-center p-3 rounded-xl border-2 cursor-pointer transition-all ${
                              selectedMeetingPoint === point.id
                                ? 'border-[#BA7B7C] bg-[#FDF8F6]' 
                                : 'border-[#E7E5E4] hover:border-[#D4A5A5]'
                            }`}
                          >
                            <input
                              type="radio"
                              name="meeting_point"
                              value={point.id}
                              checked={selectedMeetingPoint === point.id}
                              onChange={(e) => setSelectedMeetingPoint(e.target.value)}
                              className="sr-only"
                            />
                            <MapPinned className={`w-4 h-4 mr-3 ${selectedMeetingPoint === point.id ? 'text-[#BA7B7C]' : 'text-[#A8A29E]'}`} />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-[#1C1917]">{point.name}</p>
                              <p className="text-xs text-[#A8A29E]">{point.description}</p>
                            </div>
                            {selectedMeetingPoint === point.id && (
                              <CheckCircle className="w-4 h-4 text-[#BA7B7C]" />
                            )}
                          </label>
                        ))}
                      </div>
                      
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                        <p className="text-xs text-blue-800">
                          El horario exacto será confirmado por WhatsApp.
                        </p>
                      </div>
                    </div>
                  ) : (
                    /* Card or Bank Transfer = Meeting point OR Shipping */
                    <div className="space-y-3">
                      {/* Delivery Method Options */}
                      <div className="space-y-2">
                        {/* Meeting Point Option */}
                        <label 
                          className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            deliveryMethod === 'meeting_point'
                              ? 'border-[#BA7B7C] bg-[#FDF8F6]' 
                              : 'border-[#E7E5E4] hover:border-[#D4A5A5]'
                          }`}
                        >
                          <input
                            type="radio"
                            name="delivery_method"
                            value="meeting_point"
                            checked={deliveryMethod === 'meeting_point'}
                            onChange={(e) => setDeliveryMethod(e.target.value)}
                            className="sr-only"
                          />
                          <MapPinned className={`w-5 h-5 mr-3 ${deliveryMethod === 'meeting_point' ? 'text-[#BA7B7C]' : 'text-[#A8A29E]'}`} />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-[#1C1917]">Recoger en Punto de Encuentro</p>
                            <p className="text-xs text-[#A8A29E]">Sin costo de envío</p>
                          </div>
                          {deliveryMethod === 'meeting_point' && (
                            <CheckCircle className="w-5 h-5 text-[#BA7B7C]" />
                          )}
                        </label>
                        
                        {/* Home Delivery Option */}
                        <label 
                          className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            deliveryMethod === 'shipping'
                              ? 'border-[#BA7B7C] bg-[#FDF8F6]' 
                              : 'border-[#E7E5E4] hover:border-[#D4A5A5]'
                          }`}
                        >
                          <input
                            type="radio"
                            name="delivery_method"
                            value="shipping"
                            checked={deliveryMethod === 'shipping'}
                            onChange={(e) => setDeliveryMethod(e.target.value)}
                            className="sr-only"
                          />
                          <Home className={`w-5 h-5 mr-3 ${deliveryMethod === 'shipping' ? 'text-[#BA7B7C]' : 'text-[#A8A29E]'}`} />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-[#1C1917]">Envío a Domicilio</p>
                            <p className="text-xs text-[#A8A29E]">Correos de México</p>
                          </div>
                          {deliveryMethod === 'shipping' && (
                            <CheckCircle className="w-5 h-5 text-[#BA7B7C]" />
                          )}
                        </label>
                      </div>
                      
                      {/* Meeting Point Selection */}
                      {deliveryMethod === 'meeting_point' && (
                        <div className="mt-4 space-y-3">
                          <p className="text-sm text-[#57534E]">Selecciona el punto de encuentro:</p>
                          <div className="space-y-2">
                            {meetingPoints.map((point) => (
                              <label 
                                key={point.id}
                                className={`flex items-center p-3 rounded-xl border-2 cursor-pointer transition-all ${
                                  selectedMeetingPoint === point.id
                                    ? 'border-[#BA7B7C] bg-[#FDF8F6]' 
                                    : 'border-[#E7E5E4] hover:border-[#D4A5A5]'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="meeting_point"
                                  value={point.id}
                                  checked={selectedMeetingPoint === point.id}
                                  onChange={(e) => setSelectedMeetingPoint(e.target.value)}
                                  className="sr-only"
                                />
                                <MapPinned className={`w-4 h-4 mr-3 ${selectedMeetingPoint === point.id ? 'text-[#BA7B7C]' : 'text-[#A8A29E]'}`} />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-[#1C1917]">{point.name}</p>
                                  <p className="text-xs text-[#A8A29E]">{point.description}</p>
                                </div>
                                {selectedMeetingPoint === point.id && (
                                  <CheckCircle className="w-4 h-4 text-[#BA7B7C]" />
                                )}
                              </label>
                            ))}
                          </div>
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-xs text-blue-800">
                              El punto de entrega y la hora serán enviados posteriormente por WhatsApp.
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {/* Shipping Address Form */}
                      {deliveryMethod === 'shipping' && (
                        <div className="mt-4 space-y-4 bg-[#FDF8F6] rounded-xl p-4">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-medium text-[#1C1917]">Dirección de Envío</h4>
                            {user?.shipping_address && (
                              <button
                                type="button"
                                onClick={() => setShowAddressForm(!showAddressForm)}
                                className="text-xs text-[#BA7B7C] flex items-center"
                              >
                                {showAddressForm ? 'Ocultar' : 'Editar'}
                                {showAddressForm ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
                              </button>
                            )}
                          </div>
                          
                          {/* Show saved address summary if exists */}
                          {user?.shipping_address && !showAddressForm && (
                            <div className="bg-white rounded-lg p-3 border border-[#E7E5E4]">
                              <p className="text-sm text-[#57534E]">
                                {shippingAddress.street} {shippingAddress.exterior_number}
                                {shippingAddress.interior_number && `, Int. ${shippingAddress.interior_number}`}
                              </p>
                              <p className="text-sm text-[#57534E]">
                                {shippingAddress.neighborhood}, {shippingAddress.municipality}
                              </p>
                              <p className="text-sm text-[#57534E]">
                                {shippingAddress.state}, C.P. {shippingAddress.postal_code}
                              </p>
                            </div>
                          )}
                          
                          {/* Address form (always show if no saved address, or when editing) */}
                          {(!user?.shipping_address || showAddressForm) && (
                            <div className="space-y-3">
                              <div className="grid grid-cols-2 gap-3">
                                <div className="col-span-2">
                                  <label className="block text-xs text-[#57534E] mb-1">Calle *</label>
                                  <input
                                    type="text"
                                    value={shippingAddress.street}
                                    onChange={(e) => handleAddressChange('street', e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg border border-[#E7E5E4] focus:border-[#BA7B7C] focus:outline-none text-sm"
                                    placeholder="Av. Principal"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-[#57534E] mb-1">No. Exterior *</label>
                                  <input
                                    type="text"
                                    value={shippingAddress.exterior_number}
                                    onChange={(e) => handleAddressChange('exterior_number', e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg border border-[#E7E5E4] focus:border-[#BA7B7C] focus:outline-none text-sm"
                                    placeholder="123"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-[#57534E] mb-1">No. Interior</label>
                                  <input
                                    type="text"
                                    value={shippingAddress.interior_number}
                                    onChange={(e) => handleAddressChange('interior_number', e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg border border-[#E7E5E4] focus:border-[#BA7B7C] focus:outline-none text-sm"
                                    placeholder="Depto 4B"
                                  />
                                </div>
                                <div className="col-span-2">
                                  <label className="block text-xs text-[#57534E] mb-1">Colonia *</label>
                                  <input
                                    type="text"
                                    value={shippingAddress.neighborhood}
                                    onChange={(e) => handleAddressChange('neighborhood', e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg border border-[#E7E5E4] focus:border-[#BA7B7C] focus:outline-none text-sm"
                                    placeholder="Col. Centro"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-[#57534E] mb-1">Municipio *</label>
                                  <input
                                    type="text"
                                    value={shippingAddress.municipality}
                                    onChange={(e) => handleAddressChange('municipality', e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg border border-[#E7E5E4] focus:border-[#BA7B7C] focus:outline-none text-sm"
                                    placeholder="Nezahualcóyotl"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-[#57534E] mb-1">C.P. *</label>
                                  <input
                                    type="text"
                                    value={shippingAddress.postal_code}
                                    onChange={(e) => handleAddressChange('postal_code', e.target.value.replace(/\D/g, '').slice(0, 5))}
                                    className="w-full px-3 py-2 rounded-lg border border-[#E7E5E4] focus:border-[#BA7B7C] focus:outline-none text-sm"
                                    placeholder="57000"
                                    maxLength={5}
                                  />
                                </div>
                                <div className="col-span-2">
                                  <label className="block text-xs text-[#57534E] mb-1">Estado *</label>
                                  <select
                                    value={shippingAddress.state}
                                    onChange={(e) => handleAddressChange('state', e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg border border-[#E7E5E4] focus:border-[#BA7B7C] focus:outline-none text-sm bg-white"
                                  >
                                    <option value="">Selecciona un estado</option>
                                    {MEXICAN_STATES.map((state) => (
                                      <option key={state} value={state}>{state}</option>
                                    ))}
                                  </select>
                                </div>
                                <div className="col-span-2">
                                  <label className="block text-xs text-[#57534E] mb-1">Referencias</label>
                                  <input
                                    type="text"
                                    value={shippingAddress.references}
                                    onChange={(e) => handleAddressChange('references', e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg border border-[#E7E5E4] focus:border-[#BA7B7C] focus:outline-none text-sm"
                                    placeholder="Entre calle X y calle Y, casa color azul"
                                  />
                                </div>
                              </div>
                              
                              <div className="flex items-center space-x-2 text-xs text-[#A8A29E]">
                                <Truck className="w-3 h-3" />
                                <span>Envío por Correos de México</span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Order Summary before checkout */}
                <div className="bg-[#FDF8F6] rounded-xl p-4 mb-6">
                  <h4 className="text-sm font-medium text-[#1C1917] mb-3">Resumen del Pedido</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#57534E]">Método de pago:</span>
                      <span className="font-medium text-[#1C1917]">
                        {getPaymentMethodLabel(paymentMethod)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#57534E]">Método de entrega:</span>
                      <span className="font-medium text-[#1C1917]">
                        {getDeliveryMethodLabel(deliveryMethod)}
                      </span>
                    </div>
                    {deliveryMethod === 'meeting_point' && selectedMeetingPoint && (
                      <div className="flex justify-between">
                        <span className="text-[#57534E]">Punto:</span>
                        <span className="font-medium text-[#1C1917]">
                          {meetingPoints.find(p => p.id === selectedMeetingPoint)?.name || selectedMeetingPoint}
                        </span>
                      </div>
                    )}
                    {deliveryMethod === 'shipping' && (
                      <div className="flex justify-between">
                        <span className="text-[#57534E]">Paquetería:</span>
                        <span className="font-medium text-[#1C1917]">Correos de México</span>
                      </div>
                    )}
                  </div>
                </div>

                <button
                  onClick={handleCheckout}
                  disabled={checkoutLoading || cart.items.length === 0}
                  className="btn-primary w-full flex items-center justify-center space-x-2 disabled:opacity-50"
                  data-testid="checkout-btn"
                >
                  {checkoutLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <>
                      {paymentMethod === 'card' ? (
                        <CreditCard className="w-4 h-4" />
                      ) : paymentMethod === 'bank_transfer' ? (
                        <Building2 className="w-4 h-4" />
                      ) : (
                        <Banknote className="w-4 h-4" />
                      )}
                      <span>
                        {paymentMethod === 'card' 
                          ? 'Proceder al pago' 
                          : paymentMethod === 'bank_transfer'
                            ? 'Confirmar pedido'
                            : 'Confirmar pedido'}
                      </span>
                    </>
                  )}
                </button>

                <p className="text-xs text-center text-[#A8A29E] mt-4">
                  {paymentMethod === 'card' 
                    ? 'Pago seguro con Stripe'
                    : paymentMethod === 'bank_transfer'
                      ? 'Recibirás instrucciones para la transferencia'
                      : 'Pagarás al recibir tu pedido'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
